<script setup lang="ts">
  import { parseBreadcrumbs, parseTranslatedSlugs } from '@/utils/sbDataParsers'
  import type { ITranslatedSlug } from '@/components/types'
  import { trimLastSlash } from '@/utils/linkParsers'
  import { useStoryblokStore } from '@/stores/StoryBlok.store'
  import type { IStoryblokPageData } from '@/stores/types'

  const runtimeConfig = useRuntimeConfig()
  const route = useRoute()
  const { locale } = useI18n()
  const storyblokStore = useStoryblokStore()
  const dayjs = useDayjs()

  const pageStory = ref<IStoryblokPageData | undefined>()

  pageStory.value = storyblokStore.data ?? undefined

  if (!pageStory.value) {
    throw createError({
      statusCode: 404,
      statusMessage: 'global.error.pageNotFound'
    })
  }

  if (pageStory.value.status) {
    throw createError({
      statusCode: pageStory.value.status,
      statusMessage: pageStory.value.response
    })
  }

  const seoContent = pageStory.value.content?.seo

  if (seoContent) {
    useSeoMeta({
      title: seoContent?.title,
      description: seoContent?.description,
      ogSiteName: 'airBaltic.com'
    })
  }

  const translatedSlugs = pageStory.value.translated_slugs
  const parsedTranslatedSlug = computed(() => parseTranslatedSlugs(translatedSlugs, locale.value))
  const breadcrumbs = computed(() => parseBreadcrumbs(pageStory, parsedTranslatedSlug.value.name, locale.value))

  useHead(() => {
    if (!pageStory.value) {
      return {}
    }

    const links = [
      {
        rel: 'canonical',
        href: runtimeConfig.public.siteUrl + trimLastSlash(route.path)
      },
      {
        rel: 'alternate',
        hreflang: 'x-default',
        href: runtimeConfig.public.siteUrl + '/en/' + trimLastSlash(pageStory.value?.default_full_slug)
      }
    ]

    translatedSlugs.forEach((slug: ITranslatedSlug) => {
      if (!slug?.name) return

      links.push({
        rel: 'alternate',
        hreflang: slug.lang,
        href: runtimeConfig.public.siteUrl + '/' + slug.lang + '/' + trimLastSlash(slug.path)
      })
    })

    const lastModified = dayjs(pageStory.value?.updated_at).format('YYYY-MM-DD')

    return {
      meta: [
        {
          name: 'language',
          content: locale.value
        },
        {
          name: 'lastModified',
          content: lastModified
        }
      ],
      link: links,
      titleTemplate: '%s %separator'
    }
  })
</script>

<template>
  <StoryblokComponent
    v-if="pageStory"
    :blok="pageStory.content"
    :breadcrumbs="breadcrumbs"
  />
</template>
